import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import {
  CategorySection,
  OfferSource,
  VendorItem,
} from '@varo/graphql-schema-types';
import { RoutePath, useRouter } from 'expo-router';
import OfferListItem from 'features/rewards/components/OfferListItem/OfferListItem';
import { CATEGORY_ALL_LABEL } from 'features/rewards/constants/constants';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import TabDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/TabDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { isNotNilOrEmpty } from 'shared/utils/isNotNilOrEmpty/isNotNilOrEmpty';

const isRoute = (value: unknown): value is RoutePath => isNotNilOrEmpty(value);

const navigateToUpsideOffer = (
  router: ReturnType<typeof useRouter>,
  offerid?: string | null,
  merchantId?: string | null,
  coords?: { latitude: number; longitude: number } | null,
  categoryName?: string,
  options?: { autoActivate?: string },
) => {
  if (!isNotNilOrEmpty(offerid) || !isNotNilOrEmpty(merchantId)) {
    return;
  }

  const params: {
    offerid: string;
    merchantId: string;
    lat?: string;
    lng?: string;
    autoActivate?: string;
    categoryName?: string;
  } = { offerid, merchantId };

  if (coords) {
    params.lat = coords.latitude.toString();
    params.lng = coords.longitude.toString();
  }

  if (options?.autoActivate) {
    params.autoActivate = options.autoActivate;
  }

  if (categoryName && categoryName !== CATEGORY_ALL_LABEL) {
    params.categoryName = categoryName;
  }

  router.push({ pathname: '/rewards/upside-offers/[merchantId]', params });
};

interface NearbyOffersErrorProps {
  error: unknown;
  isEmptyData: boolean;
  loading: boolean;
  title: string;
  message: string;
  onRetry: () => void;
}

const NearbyOffersError = ({
  error,
  isEmptyData,
  loading,
  title,
  message,
  onRetry,
}: NearbyOffersErrorProps) => {
  if (!error && isEmptyData && !loading) {
    return (
      <EmptyStateScreen
        headlineText={title}
        bodyText={message}
        illustrationKey="OFFERS_B"
      />
    );
  }
  if (error) {
    return (
      <TabDownErrorMessage
        error={error}
        illustrationKey="HOURGLASS_A"
        tryAgainAction={onRetry}
      />
    );
  }
  return null;
};

interface ListViewScreenProps {
  categoryId?: string;
  categoryName?: string;
  onOpenSheet: () => void;
  sections: CategorySection[];
  loading: boolean;
  error: unknown;
  reload: () => void;
  coords: { latitude: number; longitude: number } | null;
  totalOffersCount?: number;
  accessibilityState: { expanded: boolean };
}

const ListViewScreen = ({
  categoryId,
  categoryName,
  onOpenSheet,
  sections,
  loading,
  error,
  reload,
  coords,
  totalOffersCount = 0,
  accessibilityState,
}: ListViewScreenProps) => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('rewards', {
    keyPrefix: 'nearbyMap',
  });

  const scrollViewRef =
    useRef<React.ComponentRef<typeof BottomSheetScrollView>>(null);

  useEffect(() => {
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  }, [categoryId]);

  const isEmptyData =
    sections.length === 0 || sections.every((s) => !s.vendors?.length);

  const handleOfferPress = (vendor: VendorItem): void => {
    if (vendor.offerSource === OfferSource.Upside) {
      navigateToUpsideOffer(
        router,
        vendor.id,
        vendor.merchantId,
        coords,
        categoryName,
      );
      return;
    }
    const route = vendor.onClick?.route;
    if (route && isRoute(route)) {
      router.push(route);
    }
  };

  const getNearbyOffersText = (): string => {
    if (loading) {
      return translatedText('findingOffers');
    }

    if (totalOffersCount === 0) {
      return translatedText('noOffersNearby');
    }

    return `${totalOffersCount} ${translatedText('offersNearby')}`;
  };

  return (
    <View
      style={styles.container}
      testID="nearby-offers-bottomsheet-wrapper"
      accessibilityState={accessibilityState}
    >
      <TouchableOpacity
        activeOpacity={1}
        onPress={onOpenSheet}
        style={styles.sheetHeaderLabelContainer}
        hitSlop={20}
        testID="nearby-offers-bottomsheet-header"
      >
        <BodyBaseText style={styles.sheetHeaderText}>
          {getNearbyOffersText()}
        </BodyBaseText>
      </TouchableOpacity>

      <BottomSheetScrollView
        ref={scrollViewRef}
        contentContainerStyle={[
          styles.scrollView,
          { paddingBottom: insets.bottom + 100 },
        ]}
      >
        {sections.map((section) => {
          const vendors = section.vendors ?? [];
          return (
            <View key={section.id} style={styles.sectionItem}>
              {section.title ? (
                <SectionDivider labelText={section.title} />
              ) : null}
              {vendors.map((vendor, index) => {
                const isLastVendor = index === vendors.length - 1;
                return (
                  <View key={vendor.id} testID={`offer-item-${index}`}>
                    <OfferListItem
                      id={vendor.id}
                      titleText={vendor.name ?? ''}
                      subtitleText={
                        vendor.cashbackRateFormatted
                          ? `${vendor.cashbackRateFormatted}`
                          : ''
                      }
                      subtitleTextSecondary={
                        vendor.offerSource === OfferSource.Upside
                          ? (vendor.tertiary ?? undefined)
                          : undefined
                      }
                      distance={
                        vendor.distanceFormatted
                          ? `${vendor.distanceFormatted} `
                          : ''
                      }
                      address={vendor.address?.addressLine1 ?? ''}
                      iconUrl={vendor.logoUrl ?? undefined}
                      onPress={() => handleOfferPress(vendor)}
                      analyticsId="nearbyOfferSelected"
                      customAttributes={{
                        offer_id: vendor.id,
                        merchant_name: vendor.name,
                        category: categoryId,
                      }}
                      isDisabled={false}
                      activateOfferAction={
                        vendor.offerSource === OfferSource.Upside &&
                        !vendor.isActive
                          ? () =>
                              navigateToUpsideOffer(
                                router,
                                vendor.id,
                                vendor.merchantId,
                                coords,
                                categoryName,
                                { autoActivate: 'true' },
                              )
                          : undefined
                      }
                      activateOfferAnalyticsId="offer-activate-clicked"
                      activateOfferCustomAttributes={{
                        offer_id: vendor.id,
                        merchant_id: vendor.merchantId,
                        merchant_name: vendor.name,
                        offer_bracket: categoryName ?? '',
                      }}
                      accessibilityLabel={`offer-item-${vendor.name}`}
                      tagText={vendor.tag?.text}
                      originalPrice={
                        vendor.pricingTier?.originalPrice?.formatted
                      }
                      discountedPrice={
                        vendor.pricingTier?.disountedPrice?.formatted
                      }
                    />
                    {!isLastVendor ? (
                      <View style={styles.divider}>
                        <HairLineDivider />
                      </View>
                    ) : null}
                  </View>
                );
              })}
            </View>
          );
        })}

        <ScreenTransitionOverlay
          isTabPage
          isLoading={loading}
          LoadingComponent={
            <View style={styles.loadingWrapper}>
              <LoadingSpinner />
            </View>
          }
          ErrorComponent={
            error || isEmptyData ? (
              <View style={styles.overlay}>
                <NearbyOffersError
                  error={error}
                  isEmptyData={isEmptyData}
                  loading={loading}
                  title={translatedText('noOffersTitle')}
                  message={translatedText('noOffersMessage')}
                  onRetry={reload}
                />
              </View>
            ) : null
          }
        />
      </BottomSheetScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    flexDirection: 'column',
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
  },
  scrollView: {
    gap: theme.spacing.s,
    backgroundColor: theme.color.background.primary,
    width: '100%',
    minHeight: '100%', // to get maximum height of the bottom sheet when content is less
  },
  sectionItem: {
    backgroundColor: theme.color.background.primary,
  },
  divider: {
    backgroundColor: theme.color.background.primary,
    marginHorizontal: theme.spacing.m,
  },
  sheetHeaderLabelContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    height: 55,
  },
  sheetHeaderText: {
    fontWeight: '600',
    color: theme.color.text.primary,
  },
  loadingWrapper: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
  },
});

export default ListViewScreen;
